import classNames from 'classnames';
import { get } from 'lodash';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FileIcon, TrashIcon } from '../icons';
import { detectFileFormat, getFileContentError } from '../utils/file-content-validation.ts';
import type { AcceptFormat } from '../utils/file-content-validation.ts';
import { imageChangeSize } from '../utils/image-change-size.ts';
import { formatValidationError } from '../utils/validation-error.ts';
import { Spinner } from './Spinner';

export type { AcceptFormat } from '../utils/file-content-validation.ts';

export interface BaseDropzoneProps {
  onFileClear: () => void;
  loading?: boolean;
  value?: string;
  error?: string;
  disabled?: boolean;
  acceptFormat?: Array<AcceptFormat>;
  maxSizeMb?: number;
  maxExpansionImage?: number;
  fileName?: string | null;
  changeExpansionImage?: boolean;
  variant?: 'document' | 'icon';
  customUnsupportedFormatError?: string;
  uploadText?: string;
  uploadTextClassName?: string;
}

export interface DropZoneSingle extends BaseDropzoneProps {
  multiple?: false | undefined;
  maxFiles?: undefined;
  onFileAccepted: (file: File) => void;
}

interface DropZoneMultiple extends BaseDropzoneProps {
  multiple: true;
  maxFiles: number;
  onFileAccepted: (files: File[]) => void;
}

export type DropzoneProps = DropZoneSingle | DropZoneMultiple;

const getConvertedImageMimeType = (acceptFormat: Array<AcceptFormat>) => {
  if (acceptFormat.includes('jpeg')) return 'image/jpeg';
  if (acceptFormat.includes('png')) return 'image/png';

  return undefined;
};

const convertHeicFile = async (file: File, toType: string) => {
  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({ blob: file, toType });
  const convertedBlob = Array.isArray(result) ? result[0] : result;
  const extension = toType === 'image/png' ? '.png' : '.jpg';
  const name = file.name.replace(/\.(heic|heif)$/i, '') || 'image';

  return new File([convertedBlob], `${name}${extension}`, {
    type: toType,
    lastModified: file.lastModified,
  });
};

export function Dropzone({
  error,
  onFileClear,
  onFileAccepted,
  value = '',
  maxFiles,
  fileName,
  customUnsupportedFormatError,
  multiple = false,
  loading = false,
  disabled = false,
  acceptFormat = ['jpeg', 'pdf', 'png', 'tiff'],
  maxSizeMb = 10,
  maxExpansionImage = 200,
  changeExpansionImage = false,
  variant = 'document',
  uploadText = 'Drop file here to upload',
  uploadTextClassName,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localError, setLocalError] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const isProcessing = loading || isConverting;

  const formatBase64 = useMemo<string[]>(() => {
    const formats: string[] = [];
    if (acceptFormat?.includes('png')) {
      formats.push('image/png');
    }
    if (acceptFormat?.includes('webp')) {
      formats.push('image/webp');
    }
    if (acceptFormat?.includes('gif')) {
      formats.push('image/gif');
    }
    if (acceptFormat?.includes('bmp')) {
      formats.push('image/bmp');
    }
    if (acceptFormat?.includes('odt')) {
      formats.push('application/vnd.oasis.opendocument.text');
    }
    if (acceptFormat?.includes('csv')) {
      formats.push('text/csv');
    }
    if (acceptFormat?.includes('jpeg')) {
      formats.push('image/jpeg');
    }
    if (acceptFormat?.includes('tiff')) {
      formats.push('image/tiff');
    }
    if (acceptFormat?.includes('pdf')) {
      formats.push('application/pdf');
    }
    if (acceptFormat?.includes('html')) {
      formats.push('text/html');
    }
    if (acceptFormat?.includes('txt')) {
      formats.push('text/plain');
    }
    if (acceptFormat?.includes('mp4')) {
      formats.push('video/mp4');
    }
    if (acceptFormat?.includes('msg')) {
      formats.push('application/vnd.ms-outlook');
    }
    if (acceptFormat?.includes('doc')) {
      formats.push(
        ...[
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
      );
    }
    if (acceptFormat?.includes('xls')) {
      formats.push(
        ...[
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
      );
    }
    if (acceptFormat?.includes('ppt')) {
      formats.push(
        ...[
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
      );
    }
    return formats;
  }, [acceptFormat]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setLocalError('');

      if (fileRejections.length > 0 && fileRejections?.[0].errors?.[0]?.message) {
        const unsupportedFormatError =
          get(fileRejections, '0.errors.0.code') === 'file-invalid-type';
        const tooLargeError = get(fileRejections, '0.errors.0.code') === 'file-too-large';

        const error = tooLargeError
          ? `File is larger than ${maxSizeMb} MB`
          : unsupportedFormatError && customUnsupportedFormatError
          ? customUnsupportedFormatError
          : fileRejections[0].errors[0].message;
        setLocalError(error);
        return;
      }

      const convertedImageMimeType = getConvertedImageMimeType(acceptFormat);
      let files = acceptedFiles;

      try {
        const fileFormats = await Promise.all(
          acceptedFiles.map(async file => ({ file, format: await detectFileFormat(file) })),
        );

        if (fileFormats.some(({ format }) => format === 'heic' || format === 'heif')) {
          if (!convertedImageMimeType) {
            setLocalError(customUnsupportedFormatError || 'This image format is not supported');
            return;
          }

          setIsConverting(true);
          files = await Promise.all(
            fileFormats.map(({ file, format }) =>
              format === 'heic' || format === 'heif'
                ? convertHeicFile(file, convertedImageMimeType)
                : file,
            ),
          );
        }
      } catch {
        setLocalError('Unable to convert the HEIC/HEIF image. Please try another file.');
        return;
      } finally {
        setIsConverting(false);
      }

      const contentErrors = await Promise.all(
        files.map(file => getFileContentError(file, acceptFormat, customUnsupportedFormatError)),
      );
      const contentError = contentErrors.find(Boolean);

      if (contentError) {
        setLocalError(contentError);
        return;
      }

      if (!formatBase64.includes(files?.[0]?.type)) {
        setLocalError(
          `File type ${files?.[0]?.type || fileRejections?.[0]?.file?.type || ''} is not support`,
        );
        return;
      }
      if (fileRejections.length > 0) {
        setLocalError(`Wrong file type: ${fileRejections?.[0]?.file?.type ?? ''}`);
        console.error('Rejected files:', fileRejections?.map(item => item.file.name).join(', '));
        return;
      }
      if (files?.[0]?.type?.includes('image') && changeExpansionImage && !multiple) {
        try {
          const changedImage = await imageChangeSize(files[0], maxExpansionImage);
          (onFileAccepted as (files: File) => void)(changedImage);
        } catch (e) {
          setLocalError(e as string);
        }
        return;
      }

      if (multiple) {
        (onFileAccepted as (files: File[]) => void)(files);
      } else {
        (onFileAccepted as (file: File) => void)(files[0]);
      }
    },
    [
      acceptFormat,
      changeExpansionImage,
      customUnsupportedFormatError,
      formatBase64,
      maxSizeMb,
      maxExpansionImage,
      multiple,
      onFileAccepted,
    ],
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onDrop && onDrop([selectedFile], []);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    maxFiles,
    useFsAccessApi: false,
    accept: {
      ...(acceptFormat?.includes('png') ? { 'image/png': ['.png'] } : {}),
      ...(acceptFormat?.includes('gif') ? { 'image/gif': ['.gif'] } : {}),
      ...(acceptFormat?.includes('webp') ? { 'image/webp': ['.webp'] } : {}),
      ...(acceptFormat?.includes('mp4') ? { 'video/mp4': ['.mp4'] } : {}),
      ...(acceptFormat?.includes('bmp') ? { 'image/bmp': ['.bmp'] } : {}),
      ...(acceptFormat?.includes('html') ? { 'text/html': ['.html', '.htm'] } : {}),
      ...(acceptFormat?.includes('txt') ? { 'text/plain': ['.txt'] } : {}),
      ...(acceptFormat?.includes('odt')
        ? { 'application/vnd.oasis.opendocument.text': ['.odt'] }
        : {}),
      ...(acceptFormat?.includes('jpeg')
        ? {
            'image/jpeg': ['.jpeg', '.jpg', '.jfif', '.pjpeg', '.pjp'],
            'image/heic': ['.heic'],
            'image/heif': ['.heif'],
          }
        : {}),
      ...(acceptFormat?.includes('png') && !acceptFormat?.includes('jpeg')
        ? {
            'image/heic': ['.heic'],
            'image/heif': ['.heif'],
          }
        : {}),
      ...(acceptFormat?.includes('tiff') ? { 'image/tiff': ['.tif', '.tiff'] } : {}),
      ...(acceptFormat?.includes('csv') ? { 'text/csv': ['.csv'] } : {}),
      ...(acceptFormat?.includes('pdf') ? { 'application/pdf': ['.pdf'] } : {}),
      ...(acceptFormat?.includes('msg') ? { 'application/vnd.ms-outlook': ['.msg'] } : {}),
      ...(acceptFormat?.includes('doc')
        ? {
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          }
        : {}),
      ...(acceptFormat?.includes('xls')
        ? {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          }
        : {}),
      ...(acceptFormat?.includes('ppt')
        ? {
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
          }
        : {}),
    },
    maxSize: 1024 * 1024 * maxSizeMb, // 10 mb
    disabled: disabled || isProcessing || Boolean(value),
  });

  const inputAccept = useMemo<string>(() => {
    let formats: string[] = [];
    if (acceptFormat?.includes('png')) {
      formats = formats.concat(['.png']);
    }
    if (acceptFormat?.includes('gif')) {
      formats = formats.concat(['.gif']);
    }
    if (acceptFormat?.includes('bmp')) {
      formats = formats.concat(['.bmp']);
    }
    if (acceptFormat?.includes('odt')) {
      formats = formats.concat(['.odt']);
    }
    if (acceptFormat?.includes('html')) {
      formats = formats.concat(['.htm', '.html']);
    }
    if (acceptFormat?.includes('webp')) {
      formats = formats.concat(['.webp']);
    }
    if (acceptFormat?.includes('mp4')) {
      formats = formats.concat(['.mp4']);
    }
    if (acceptFormat?.includes('ppt')) {
      formats = formats.concat(['.ppt', '.pptx']);
    }
    if (acceptFormat?.includes('txt')) {
      formats = formats.concat(['.txt']);
    }
    if (acceptFormat?.includes('jpeg')) {
      formats = formats.concat(['.jpeg', '.jpg', '.jfif', '.pjpeg', '.pjp', '.heic', '.heif']);
    } else if (acceptFormat?.includes('png')) {
      formats = formats.concat(['.heic', '.heif']);
    }
    if (acceptFormat?.includes('tiff')) {
      formats = formats.concat(['.tif', '.tiff']);
    }
    if (acceptFormat?.includes('csv')) {
      formats = formats.concat(['.csv']);
    }
    if (acceptFormat?.includes('pdf')) {
      formats = formats.concat(['.pdf']);
    }
    if (acceptFormat?.includes('doc')) {
      formats = formats.concat(['.doc', '.docx']);
    }
    if (acceptFormat?.includes('xls')) {
      formats = formats.concat(['.xls', '.xlsx']);
    }
    if (acceptFormat?.includes('msg')) {
      formats = formats.concat(['.msg']);
    }
    return formats.join(',');
  }, [acceptFormat]);

  const isImage = useMemo(() => {
    if (!value) return false;
    let formats: string[] = [];
    if (acceptFormat?.includes('jpeg')) {
      formats = formats.concat(['.jpeg', '.jpg', '.jfif', '.pjpeg', '.pjp']);
    }
    if (acceptFormat?.includes('tiff')) {
      formats = formats.concat(['.tif', '.tiff']);
    }
    if (acceptFormat?.includes('webp')) {
      formats = formats.concat(['.webp']);
    }
    if (acceptFormat?.includes('png')) {
      formats = formats.concat(['.png']);
    }
    if (acceptFormat?.includes('gif')) {
      formats = formats.concat(['.gif']);
    }
    if (acceptFormat?.includes('bmp')) {
      formats = formats.concat(['.bmp']);
    }
    if (value.includes(':image/')) return true;

    const lower = value.toLowerCase().split('?')[0].split('#')[0];
    return formats.some(ext => lower.endsWith(ext));
  }, [acceptFormat, value]);

  const isPdf = useMemo(() => {
    if (!value) return false;
    let formats: string[] = [];
    if (acceptFormat?.includes('pdf')) {
      formats = formats.concat(['.pdf']);
    }

    if (value.includes(':application/pdf')) return true;

    const lower = value.toLowerCase().split('?')[0].split('#')[0];
    return formats.some(ext => lower.endsWith(ext));
  }, [acceptFormat, value]);

  return (
    <div className="w-full flex flex-col gap-2">
      {variant === 'icon' && (
        <div
          className="flex gap-4 h-[64px] items-center select-none"
          {...getRootProps()}
          onClick={undefined}
        >
          <span className="rounded-lg border p-1 h-[64px] w-[64px] min-w-[64px]">
            <input
              {...getInputProps()}
              accept={inputAccept}
              ref={inputRef}
              onChange={handleFileChange}
            />
            {isProcessing ? (
              <Spinner />
            ) : (
              <div
                className={`w-full flex h-[56px] items-center text-center justify-${
                  value ? 'between' : 'center'
                }`}
              >
                {value ? (
                  <>
                    {isImage ? (
                      <img src={value} alt="Loading..." className={classNames('max-h-[56px]')} />
                    ) : isPdf ? (
                      <iframe
                        src={value}
                        className="max-h-[56px] max-w-[56px] aspect-[0.64] overflow-hidden"
                      ></iframe>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <span className="text-gray-500 text-xs leading-5">No image</span>
                )}
              </div>
            )}
          </span>
          <span className="flex flex-col w-full gap-0.5">
            <span className="text-sm leading-[22px] text-blue-600 font-[Inter]">
              <span
                className={classNames('pr-2 hover:text-blue-500', {
                  'cursor-pointer': !disabled,
                })}
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.click();
                  }
                }}
              >
                Upload {value ? 'new ' : ''}image
              </span>
              {value && (
                <span
                  className={classNames('pl-2 border-l hover:text-blue-500', {
                    'cursor-pointer': !disabled,
                  })}
                  onClick={onFileClear}
                >
                  Delete
                </span>
              )}
            </span>
            <span className="text-xs leading-5 text-gray-600">
              Style guide: PNG/JPEG file format. With transparent or white background. Square aspect
              ratio, minimum {maxExpansionImage}px/{maxExpansionImage}px
            </span>
          </span>
        </div>
      )}
      {variant === 'document' && (
        <div
          className={classNames(
            'w-full min-h-[91px] max-h-[250px] rounded-lg select-none',
            'flex items-center justify-center',
            'border border-gray-900 border-opacity-16 border-dashed',
            {
              'hover:cursor-pointer': !value && !disabled,
            },
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} accept={inputAccept} />
          {isProcessing ? (
            <Spinner />
          ) : (
            <div
              className={`w-full flex flex-${value ? 'row' : 'col '} gap-4 items-center justify-${
                value ? 'between' : 'center'
              }`}
            >
              {value ? (
                <>
                  {isImage && (
                    <img
                      src={value}
                      alt="Loading..."
                      className={classNames('max-h-[82px] rounded ml-1')}
                    />
                  )}
                  {isPdf && (
                    <iframe
                      src={value}
                      title={fileName || 'PDF preview'}
                      className="w-[130px] h-[180px] py-1 pl-1"
                    />
                  )}
                  {!isImage && !isPdf && (
                    <img
                      src={FileIcon}
                      alt="Loading..."
                      className={classNames('max-h-[82px] rounded ml-1')}
                    />
                  )}
                  {fileName && (
                    <div className="flex-1" title={fileName}>
                      <div className="max-w-40 truncate pl-2">{fileName}</div>
                    </div>
                  )}
                  <img
                    src={TrashIcon}
                    alt="Loading"
                    className={classNames('mr-4', { 'cursor-pointer': !disabled })}
                    onClick={onFileClear}
                  />
                </>
              ) : (
                <>
                  <p
                    className={classNames(
                      'text-gray-500 text-[14px] leading-[22px] max-w-[320px] text-center',
                      uploadTextClassName,
                    )}
                  >
                    {uploadText}
                  </p>
                  <label
                    className={classNames('text-gray-900 text-[14px] leading-[22px] underline', {
                      'cursor-pointer': !disabled,
                    })}
                  >
                    Browse your device
                  </label>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {(error || localError) && (
        <span className="text-error-red text-sm">
          {formatValidationError(
            error || localError,
            'upload a file in one of the accepted formats',
          )}
        </span>
      )}
    </div>
  );
}
